import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import HeaderWithTooltip from '@/components/UI/TableListEditable/HeaderWithTooltip/HeaderWithTooltip';
import TableListEditableNew from '@/components/UI/TableListEditable/TableListEditableNew';
import { HELP_AREA, VEHICLE_STATUS_COMMON_COUNT_COLUMNS, VEHICLE_STATUS_OVERVIEW_COLUMNS } from '@/helpers/constants';
import { parseObjectNegativeValuesToBlank } from '@/helpers/utils';
import useError from '@/hooks/useError';
import { getCampaignInfo } from '@/redux/Campaign/campaign.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { setAreaHelp } from '@/redux/Help/help.action';
import campaignServices from '@/services/campaignServices';
import styles from './PlantFeedback.module.scss';

const PlantFeedback = (props) => {
  const { headerLanguage, campaignInfo, setHelpArea } = props;
  const { t } = useTranslation();
  const { handleCriticalError } = useError();
  const { id, version } = campaignInfo;

  const [feedbackData, setFeedbackData] = useState([]);

  const columns = useMemo(() => [
    { name: 'mainCr', title: t('plantFeedback.mainCr') },
    { name: 'subCr', title: t('plantFeedback.subCr') },
    { name: 'campaign', title: t('feedback.campaignNumber') },
    { name: 'dynamic', title: t('plantFeedback.dynamic') },
    { name: 'blockingLocation', title: t('plantFeedback.blockingLocation') },
    {
      name: 'estimatedCars',
      title: t('plantFeedback.estimatedCars'),
      headerComponent: HeaderWithTooltip,
      headerComponentParams: { tooltipText: t('plantFeedback.estimatedCarsTooltip'), isWarningTooltip: false },
    },
    { name: 'validatedCars', title: t('plantFeedback.validatedCars') },
    { name: 'noRework', title: t('plantFeedback.noRework') },
    {
      name: VEHICLE_STATUS_OVERVIEW_COLUMNS.REWORK_NOT_STARTED,
      title: t('plantFeedback.reworkNotStarted'),
      headerComponent: HeaderWithTooltip,
      headerComponentParams: { tooltipText: t('plantFeedback.reworkNotStartedTooltip'), isWarningTooltip: false },
    },
    { name: 'reworkOpen', title: t('plantFeedback.reworkOpen') },
    { name: 'reworkFinished', title: t('plantFeedback.reworkFinished') },
    { name: 'okWithoutRework', title: t('feedback.withoutRework') },
    { name: 'reworkInField', title: t('plantFeedback.reworkInField') },
  ], [t]);

  const tableData = useMemo(() => {
    if (!feedbackData) return [];

    const rows = [];

    const MAIN_CR_FIELDS = [...VEHICLE_STATUS_COMMON_COUNT_COLUMNS, VEHICLE_STATUS_OVERVIEW_COLUMNS.ESTIMATED_CARS];
    rows.push({
      key: feedbackData.crId,
      mainCr: feedbackData.crId,
      ...parseObjectNegativeValuesToBlank(MAIN_CR_FIELDS, feedbackData),
    });

    feedbackData.subCrs?.forEach((subCr) => {
      rows.push({
        key: `${feedbackData.crId}_${subCr.subCrId}`,
        subCr: subCr.subCrId,
        isSubCRChildren: true,
        ...parseObjectNegativeValuesToBlank(VEHICLE_STATUS_COMMON_COUNT_COLUMNS, subCr),
      });

      subCr.campaigns?.forEach((camp) => {
        rows.push({
          key: `${feedbackData.crId}_${subCr.subCrId}_${camp.campaign}`,
          campaign: camp.campaign,
          dynamic: camp.dynamic
            ? t('plantFeedback.dynamicYes', { lng: headerLanguage?.toLowerCase() })
            : t('plantFeedback.dynamicNo', { lng: headerLanguage?.toLowerCase() }),
          thirdLevelRow: true,
          ...parseObjectNegativeValuesToBlank(VEHICLE_STATUS_COMMON_COUNT_COLUMNS, camp),
        });

        camp.blockingLocations?.forEach((loc) => {
          rows.push({
            key: `${feedbackData.crId}_${subCr.subCrId}_${camp.campaign}_${loc.location}`,
            blockingLocation: loc.location,
            fourthLevelRow: true,
            ...parseObjectNegativeValuesToBlank(VEHICLE_STATUS_COMMON_COUNT_COLUMNS, loc),
          });
        });
      });
    });

    return rows;
  }, [feedbackData, t, headerLanguage]);

  useEffect(() => {
    campaignServices.getVehiclesStatus(id, version)
      .then((res) => setFeedbackData(res))
      .catch(handleCriticalError);
  }, [handleCriticalError, id, version]);

  return (
    <div className={styles.PlantFeedback}>
      <AccordionPanel
        handleClick={() => setHelpArea(HELP_AREA.PLANT_FEEDBACK)}
      >
        <TableListEditableNew
          list={tableData}
          columns={columns}
          isPaginated={false}
        />
      </AccordionPanel>
    </div>
  );
};

const mapStateToProps = (state) => ({
  headerLanguage: getHeaderLanguage(state),
  campaignInfo: getCampaignInfo(state),
});

const actionCreators = {
  setHelpArea: setAreaHelp,
};

export default (connect(mapStateToProps, actionCreators)(PlantFeedback));
